/**
 * format.js — one formatter per kind of value, for the whole Research section.
 * =============================================================================
 * These replace ad-hoc formatters that were defined per-component and drifted:
 * `_resFmt` in ResearcherPage prefixed EVERY value with `$`, and was the default
 * formatter for IntelStat, so a hash rate rendered as "$878", a mempool depth as
 * "$79.2K" and a market count as "$4.5K markets".
 *
 * Rules, applied once here instead of at every call site:
 *  - Real minus sign (U+2212), not a hyphen.
 *  - Sign goes BEFORE the currency symbol: −$31.00, never $-31.00.
 *  - null/undefined render as an em dash, never as 0. A missing number and a
 *    zero are different facts and the UI must not conflate them.
 *  - Currency formatting is opt-in. If a value is not money, it does not get a
 *    dollar sign.
 */

const DASH = "—";
const MINUS = "−"; // U+2212 MINUS SIGN

/** True when an ISO string carries a timezone designator. */
const HAS_OFFSET = /[zZ]|[+-]\d\d:?\d\d$/;

/**
 * Parse an API timestamp to epoch ms, treating offset-less strings as UTC.
 *
 * The backend now stamps every datetime with an offset, but this stays as the
 * client-side guard: any endpoint not yet migrated, or a cached response from
 * before the fix, would otherwise be read as LOCAL time. That is what produced
 * "-297m" feed ages at UTC-5.
 */
export const parseApiTime = (s) => {
  if (!s) return NaN;
  return Date.parse(HAS_OFFSET.test(s) ? s : `${s}Z`);
};

/**
 * Human age of a timestamp. Clamped at zero: a future timestamp means a clock
 * or timezone problem, and showing "in 5 hours" for a fresh feed is worse than
 * showing "just now". Returns the em dash for anything unparseable.
 */
export const ago = (s) => {
  const t = parseApiTime(s);
  if (!Number.isFinite(t)) return DASH;
  const m = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};

const isNum = (v) => v != null && v !== "" && Number.isFinite(Number(v));

/** Integer with thousands separators: 273831 -> "273,831". */
export const fmtInt = (v) =>
  !isNum(v) ? DASH : Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 });

/** Signed percentage: 1.291 -> "+1.29%", -0.5 -> "−0.50%". */
export const fmtPct = (v, d = 2) => {
  if (!isNum(v)) return DASH;
  const n = Number(v);
  return `${n < 0 ? MINUS : "+"}${Math.abs(n).toFixed(d)}%`;
};

/** Unsigned percentage, for rates that are never negative: 73.5 -> "73.5%". */
export const fmtPctPlain = (v, d = 1) =>
  !isNum(v) ? DASH : `${Number(v).toFixed(d)}%`;

/** Money, sign before the symbol: -31 -> "−$31.00". */
export const fmtUsd = (v) => {
  if (!isNum(v)) return DASH;
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${n < 0 ? MINUS : ""}$${abs}`;
};

/** Money, abbreviated: 589500 -> "$589.5K". For money ONLY. */
export const fmtUsdCompact = (v) => {
  if (!isNum(v)) return DASH;
  const n = Number(v);
  const sign = n < 0 ? MINUS : "";
  const a = Math.abs(n);
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(1)}K`;
  return `${sign}$${a.toFixed(0)}`;
};

/**
 * Abbreviated NON-currency number: 589500 -> "589.5K", 878 -> "878".
 * This is the default for counts, rates and depths. If you want a dollar sign,
 * ask for one with fmtUsdCompact.
 */
export const fmtCompact = (v) => {
  if (!isNum(v)) return DASH;
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(v));
};

/** A ratio in [0,1] as a percentage: 0.735 -> "73.5%". */
export const fmtRate = (r) => (!isNum(r) ? DASH : `${(Number(r) * 100).toFixed(1)}%`);

/** Fixed-decimal number, no unit: 0.1551 -> "0.155". */
export const fmtNum = (v, d = 2) => (!isNum(v) ? DASH : Number(v).toFixed(d));

export { DASH, MINUS };
